// Step-by-step breakdowns for interactive tutorials
// This file now contains only type definitions and imports from tutorial files

interface Example {
  problem: string;
  solution: string;
  explanation: string;
  intermediateSteps?: Array<{
    label: string;
    answer: string;
    hint?: string;
  }>;
}

export interface Step {
  stepNumber?: number;
  title: string;
  explanation: string;
  tips?: string[];
  examples?: {
    simple: Example;
    intermediate: Example;
  };
  practice?: {
    problem: string;
    question: string;
    answer: string;
    hint?: string;
    intermediateSteps?: {
      label: string;
      answer: string;
      hint?: string;
    }[];
  };
}

export interface TechniqueBreakdown {
  title?: string;
  description?: string;
  steps: Step[];
  finalExample?: {
    problem: string;
    fullSolution: string;
  };
}

// Import all tutorial modules
import { newTechniqueBreakdowns } from "./tutorials/newTechniqueBreakdowns";
import { vedicMathTutorials } from "./tutorials/vedicMathTutorials";
import { algebraTutorials } from "./tutorials/algebraTutorials";
import { universalTricksTutorials } from "./tutorials/universalTricksTutorials";
import { russianMathTutorials } from "./tutorials/russianMathTutorials";
import { problemSolvingTutorials } from "./tutorials/problemSolvingTutorials";
import { numberTheoryTutorials } from "./tutorials/numberTheoryTutorials";
import { geometryTutorials } from "./tutorials/geometryTutorials";

// Legacy empty object for backwards compatibility
export const techniqueBreakdowns: Record<string, TechniqueBreakdown> = {};

/**
 * Get a technique breakdown by ID from all available tutorial sources
 * @param techniqueId - The ID of the technique to retrieve
 * @returns The technique breakdown or undefined if not found
 */
export function getTechniqueBreakdown(
  techniqueId: string,
): TechniqueBreakdown | undefined {
  // Check all tutorial sources in order
  return (
    newTechniqueBreakdowns[techniqueId] ||
    vedicMathTutorials[techniqueId] ||
    algebraTutorials[techniqueId] ||
    universalTricksTutorials[techniqueId] ||
    russianMathTutorials[techniqueId] ||
    problemSolvingTutorials[techniqueId] ||
    numberTheoryTutorials[techniqueId] ||
    geometryTutorials[techniqueId]
  );
}

/**
 * Get all available technique names across all tutorial sources
 * @returns Array of all available technique names
 */
export function getAllTechniqueNames(): string[] {
  return [
    ...Object.keys(newTechniqueBreakdowns),
    ...Object.keys(vedicMathTutorials),
    ...Object.keys(algebraTutorials),
    ...Object.keys(universalTricksTutorials),
    ...Object.keys(russianMathTutorials),
    ...Object.keys(problemSolvingTutorials),
    ...Object.keys(numberTheoryTutorials),
    ...Object.keys(geometryTutorials),
  ];
}
